import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  Paper,
  IconButton
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Devices as DevicesIcon,
  ChevronRight as DrillIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useIsMobile } from '../../../../hooks/useResponsive';
import LocationTypeIcon from '../shared/LocationTypeIcon';
import LocationEmptyState from '../shared/LocationEmptyState';
import LocationDetailPanel from '../detail/LocationDetailPanel';
import {
  getLocationId,
  getLocationName,
  getLocationTypeId,
  getLocationTypeName
} from '../../utils/locationTransformers';
import { getLocationTypeConfig, DETAIL_PANEL_SX, MOBILE_DRAWER_PAPER_SX } from '../../utils/locationConstants';

/**
 * Breadcrumb Drill-Down View
 * List-based "file browser" - click folders to drill in, click leaves to select
 */
const BreadcrumbDrillView = ({
  childrenAtLevel,
  drillBreadcrumbPath,
  loading,
  error,
  selectedLocation,
  onDrillInto,
  onDrillUp,
  onDrillToLevel,
  onLocationSelect,
  onRetry,
  detailPanelRef,
  detailPanelProps,
  t
}) => {
  const isMobile = useIsMobile();

  if (error) {
    return <LocationEmptyState type="error" errorMessage={error} onAction={onRetry} t={t} />;
  }

  const handleItemClick = (location) => {
    const hasChildren = location.children && location.children.length > 0;
    if (hasChildren) {
      onDrillInto(getLocationId(location));
    }
    onLocationSelect(location);
  };

  const canGoBack = drillBreadcrumbPath.length > 0;

  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      height: { xs: 'auto', md: 'calc(100vh - 230px)' },
      flexDirection: { xs: 'column', md: 'row' }
    }}>
      {/* Main drill area */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Breadcrumb navigation */}
        <Paper
          variant="outlined"
          sx={{
            m: { xs: 1, sm: 2 },
            mb: 0,
            p: { xs: 1, sm: 1.5 },
            borderRadius: 1,
            bgcolor: 'action.hover'
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            maxItems={isMobile ? 3 : 6}
            itemsBeforeCollapse={1}
            itemsAfterCollapse={2}
            aria-label="Location breadcrumb navigation"
          >
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() => onDrillToLevel(-1)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            >
              <HomeIcon fontSize="small" />
              {!isMobile && 'All Locations'}
            </Link>
            {drillBreadcrumbPath.map((crumb, idx) => {
              const isLast = idx === drillBreadcrumbPath.length - 1;
              return isLast ? (
                <Typography key={crumb.id} variant="body2" color="text.primary" fontWeight={600} noWrap>
                  {crumb.name}
                </Typography>
              ) : (
                <Link
                  key={crumb.id}
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => onDrillToLevel(idx)}
                  sx={{ cursor: 'pointer' }}
                  noWrap
                >
                  {crumb.name}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Paper>

        {/* List of children */}
        <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 0, sm: 2 }, py: 1 }}>
          {childrenAtLevel.length === 0 ? (
            <LocationEmptyState
              type="no_results"
              t={t}
              onAction={canGoBack ? () => onDrillUp() : undefined}
            />
          ) : (
            <List disablePadding>
              {/* Back row */}
              {canGoBack && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => onDrillUp()} sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        <BackIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary">
                            .. Back
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </>
              )}

              {childrenAtLevel.map((location, idx) => {
                const locId = getLocationId(location);
                const typeId = getLocationTypeId(location);
                const config = getLocationTypeConfig(typeId);
                const hasChildren = location.children && location.children.length > 0;
                const isSelected = selectedLocation && String(getLocationId(selectedLocation)) === String(locId);
                const childCount = location.children?.length || 0;

                return (
                  <React.Fragment key={locId}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleItemClick(location)}
                        sx={{
                          py: { xs: 1.5, sm: 1 },
                          minHeight: { xs: 56, sm: 48 },
                          borderLeft: 3,
                          borderColor: isSelected ? config.color : 'transparent'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 44 }}>
                          <LocationTypeIcon typeId={typeId} size={24} withBackground={isSelected} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              fontWeight={isSelected ? 600 : 400}
                              noWrap
                            >
                              {getLocationName(location)}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                {getLocationTypeName(location)}
                              </Typography>
                              {(location.deviceCount > 0) && (
                                <Chip
                                  icon={<DevicesIcon />}
                                  label={location.deviceCount}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                                />
                              )}
                            </Box>
                          }
                        />

                        {/* Right side: child count + drill indicator */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                          {hasChildren && (
                            <Chip
                              label={childCount}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ height: 22, minWidth: 28 }}
                            />
                          )}
                          {hasChildren && (
                            <DrillIcon color="action" fontSize="small" />
                          )}
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {idx < childrenAtLevel.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Detail Panel - desktop */}
      {selectedLocation && !isMobile && (
        <Box ref={detailPanelRef} sx={DETAIL_PANEL_SX}>
          <LocationDetailPanel
            selectedLocation={selectedLocation}
            {...detailPanelProps}
          />
        </Box>
      )}

      {/* Detail Panel - mobile drawer */}
      {selectedLocation && isMobile && (
        <Drawer
          anchor="bottom"
          open={!!selectedLocation}
          onClose={() => onLocationSelect(null)}
          PaperProps={{ sx: MOBILE_DRAWER_PAPER_SX }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              onClick={() => onLocationSelect(null)}
              size="small"
              aria-label="Close detail panel"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <LocationDetailPanel
            selectedLocation={selectedLocation}
            {...detailPanelProps}
          />
        </Drawer>
      )}
    </Box>
  );
};

export default BreadcrumbDrillView;
